import { useState, useRef, useEffect, useCallback } from "react";
import type { PhaseType } from "../usePhaseAudio";

const STEPS = [
  {
    number: "I",
    title: "無力の承認",
    subtitle: "Surrender to Reality",
    text: "私は、自分ひとりの力では、外部刺激への依存から自由になれないことを認める。この認識は敗北ではない。——それは、より深い力への入口である。",
    phase: "PHASE I" as PhaseType,
  },
  {
    number: "II",
    title: "高次の力への信頼",
    subtitle: "Trust in Greater Intelligence",
    text: "自我（Ego）を超えた知性が存在することを信じる。それは神でも科学でも、あるいは宇宙の秩序そのものでも構わない。私はその流れに身を委ねる。",
    phase: "PHASE I" as PhaseType,
  },
  {
    number: "III",
    title: "意志の委託",
    subtitle: "Conscious Surrender",
    text: "私の意志と人生の方向性を、より大きな叡智に委ねることを選ぶ。コントロールを手放すことで、初めて本当の自由が現れる。",
    phase: "PHASE I" as PhaseType,
  },
  {
    number: "IV",
    title: "道徳的棚卸し",
    subtitle: "Fearless Moral Inventory",
    text: "恐れることなく、徹底的に自己を観察する。私の影——怒り、恐怖、欲望、後悔——を直視し、それらを意識の光の下に置く。",
    phase: "PHASE II" as PhaseType,
  },
  {
    number: "V",
    title: "告白と開示",
    subtitle: "Radical Honesty",
    text: "神に、自分自身に、そして信頼できる一人の人間に、自らの本当の姿を打ち明ける。秘密は闇の中でのみ力を持つ。光の中で、それは消える。",
    phase: "PHASE II" as PhaseType,
  },
  {
    number: "VI",
    title: "変化の準備",
    subtitle: "Readiness for Transformation",
    text: "私を縛るすべてのパターンを、手放す準備をする。「変わりたい」という願望から、「変わる」という決意へ。この境界線を越える。",
    phase: "PHASE II" as PhaseType,
  },
  {
    number: "VII",
    title: "欠点の昇華",
    subtitle: "Alchemical Transformation",
    text: "短所を力に変える錬金術を実践する。恐れは洞察に、怒りは情熱に、依存は探求心に変容する——これが神経回路の意図的な再設計である。",
    phase: "PHASE II" as PhaseType,
  },
  {
    number: "VIII",
    title: "修復のリスト",
    subtitle: "Inventory of Harm",
    text: "私が傷つけたすべての人のリストを作る。そして、できる限り修復する意志を持つ。過去は変えられない。しかし、その意味は変えられる。",
    phase: "PHASE II" as PhaseType,
  },
  {
    number: "IX",
    title: "直接的な修復",
    subtitle: "Making Amends",
    text: "可能な限り、直接的に修復を行う。ただし、それが他者をさらに傷つける場合を除いて。この行為は自己罰ではない——それは愛からの行動である。",
    phase: "PHASE II" as PhaseType,
  },
  {
    number: "X",
    title: "継続的な棚卸し",
    subtitle: "Daily Consciousness",
    text: "毎日、自己を観察し続ける。間違いを犯したときは、即座に認める。完璧である必要はない——ただ、意識的であり続けること。",
    phase: "PHASE III" as PhaseType,
  },
  {
    number: "XI",
    title: "瞑想と接続",
    subtitle: "Meditation & Communion",
    text: "祈りと瞑想を通じて、より深い意識との接続を深める。求めるのは力を増すことではなく、この宇宙の意志を知り、それを実行する力を得ることだ。",
    phase: "PHASE III" as PhaseType,
  },
  {
    number: "XII",
    title: "覚醒の奉仕",
    subtitle: "Awakening & Service",
    text: "この変容の体験を経て、私は他の人々にこのメッセージを伝える義務を持つ。自らが受けた恩寵は、与えることでのみ完成する。これが螺旋の完成である。",
    phase: "PHASE III" as PhaseType,
  },
];

const PHASE_STYLES: Record<PhaseType, {
  numberColor: string;
  titleColor: string;
  textColor: string;
  lineColor: string;
  labelColor: string;
  labelBg: string;
  borderColor: string;
}> = {
  idle: {
    numberColor: "rgba(255,220,130,0.65)",
    titleColor: "rgba(255,245,215,0.82)",
    textColor: "rgba(255,255,255,0.38)",
    lineColor: "rgba(201,168,76,0.1)",
    labelColor: "rgba(255,215,110,0.55)",
    labelBg: "rgba(201,168,76,0.06)",
    borderColor: "rgba(201,168,76,0.18)",
  },
  // PHASE I: 天界の炎と崩壊の受容 — 深紅×琥珀
  "PHASE I": {
    numberColor: "rgba(220,90,60,0.82)",
    titleColor: "rgba(255,215,195,0.88)",
    textColor: "rgba(255,225,210,0.5)",
    lineColor: "rgba(185,55,32,0.18)",
    labelColor: "rgba(220,90,60,0.72)",
    labelBg: "rgba(180,42,20,0.1)",
    borderColor: "rgba(185,58,36,0.26)",
  },
  // PHASE II: 大地の浄化と解体 — 琥珀×金
  "PHASE II": {
    numberColor: "rgba(230,150,48,0.82)",
    titleColor: "rgba(255,228,168,0.92)",
    textColor: "rgba(255,235,185,0.5)",
    lineColor: "rgba(205,125,32,0.18)",
    labelColor: "rgba(230,150,48,0.72)",
    labelBg: "rgba(185,105,22,0.1)",
    borderColor: "rgba(205,130,36,0.26)",
  },
  // PHASE III: 天界の光と再統合 — 白金×天の光
  "PHASE III": {
    numberColor: "rgba(255,232,115,0.92)",
    titleColor: "rgba(255,252,230,0.97)",
    textColor: "rgba(255,248,215,0.62)",
    lineColor: "rgba(255,215,82,0.2)",
    labelColor: "rgba(255,232,115,0.82)",
    labelBg: "rgba(255,205,65,0.08)",
    borderColor: "rgba(255,220,88,0.32)",
  },
};

const PHASE_LABELS: Record<string, { en: string; ja: string }> = {
  "PHASE I":  { en: "PHASE I · SURRENDER",      ja: "崩壊の受容" },
  "PHASE II": { en: "PHASE II · DECONSTRUCTION", ja: "内面の解体" },
  "PHASE III":{ en: "PHASE III · REINTEGRATION", ja: "再統合"    },
};

interface TwelveStepsScrollProps {
  onComplete: () => void;
  onPhaseChange: (phase: PhaseType) => void;
  currentPhase: PhaseType;
}

export default function TwelveStepsScroll({
  onComplete,
  onPhaseChange,
  currentPhase,
}: TwelveStepsScrollProps) {
  const [readProgress, setReadProgress] = useState(0);
  const [allRead, setAllRead] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  // スクロール位置から「何番目まで読んだか」を計算
  const [revealedUpTo, setRevealedUpTo] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastPhaseRef = useRef<PhaseType>("PHASE I");

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const total = scrollHeight - clientHeight;
    const progress = total > 0 ? scrollTop / total : 1;
    setReadProgress(Math.min(progress, 1));
    if (progress >= 0.95) setAllRead(true);

    // 何番目のステップまで表示するか（スクロール位置から計算）
    const revealed = Math.floor(progress * (STEPS.length + 2));
    setRevealedUpTo(Math.min(revealed, STEPS.length));

    // フェーズ判定
    const stepIdx = Math.min(Math.floor(progress * STEPS.length), STEPS.length - 1);
    const newPhase = STEPS[stepIdx].phase;
    if (newPhase !== lastPhaseRef.current) {
      lastPhaseRef.current = newPhase;
      onPhaseChange(newPhase);
    }
  }, [onPhaseChange]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    // 初期表示時に最初の数ステップを見せる
    setRevealedUpTo(3);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSign = () => {
    if (!allRead || signingIn) return;
    setAgreed(true);
    setSigningIn(true);
    setTimeout(() => onComplete(), 2200);
  };

  const style = PHASE_STYLES[currentPhase] ?? PHASE_STYLES["PHASE I"];

  const getPhaseBreak = (idx: number): string | null => {
    if (idx === 0) return "PHASE I";
    if (idx === 3) return "PHASE II";
    if (idx === 9) return "PHASE III";
    return null;
  };

  return (
    <div className="flex flex-col" style={{ height: "100%", minHeight: 0 }}>
      {/* ── スクロール本文 ── */}
      <div
        ref={scrollRef}
        style={{
          flex: "1 1 0",
          overflowY: "auto",
          paddingRight: "4px",
          scrollbarWidth: "thin",
          scrollbarColor: `${style.lineColor} transparent`,
          minHeight: 0,
        }}
      >
        {/* ヘッダー */}
        <div className="text-center mb-10 pt-1">
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: style.labelColor,
            fontSize: "9px",
            letterSpacing: "0.6em",
            marginBottom: "10px",
            transition: "color 2s ease",
          }}>
            ZEN GENESIS OS · DIVINE CODEX
          </div>
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: style.titleColor,
            fontSize: "clamp(1.3rem, 2.5vw, 1.9rem)",
            fontWeight: 300,
            letterSpacing: "0.1em",
            lineHeight: 1.4,
            marginBottom: "6px",
            transition: "color 2s ease",
          }}>
            神の啓示
          </h2>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: style.labelColor,
            fontSize: "10px",
            letterSpacing: "0.4em",
            fontStyle: "italic",
            opacity: 0.7,
            transition: "color 2s ease",
          }}>
            The Twelve Commandments of ZEN GENESIS
          </div>
          <div className="mx-auto mt-5" style={{
            width: "36px", height: "1px",
            background: style.borderColor,
            transition: "background 2s ease",
          }} />
        </div>

        {/* 前文 */}
        <div className="mb-8 text-center max-w-lg mx-auto" style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: style.textColor,
          fontSize: "12.5px",
          lineHeight: 2,
          letterSpacing: "0.08em",
          fontStyle: "italic",
          transition: "color 2s ease",
        }}>
          これは命令ではない。<br />
          これは、あなたが既に知っていたことの——<br />
          言語化である。
        </div>

        {/* 12ステップ */}
        <div className="space-y-5">
          {STEPS.map((step, idx) => {
            const phaseBreak = getPhaseBreak(idx);
            const isRevealed = idx < revealedUpTo;
            const stepStyle = PHASE_STYLES[step.phase];

            return (
              <div key={step.number}>
                {/* フェーズ区切り */}
                {phaseBreak && (
                  <div
                    className="flex items-center gap-3 mb-5"
                    style={{
                      opacity: isRevealed || idx === 0 ? 1 : 0.25,
                      transition: "opacity 1.2s ease",
                    }}
                  >
                    <div className="flex-1 h-px" style={{ background: stepStyle.borderColor }} />
                    <div
                      className="px-3 py-1 flex items-center gap-2"
                      style={{
                        border: `1px solid ${stepStyle.borderColor}`,
                        background: stepStyle.labelBg,
                      }}
                    >
                      <span style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        color: stepStyle.labelColor,
                        fontSize: "7.5px",
                        letterSpacing: "0.45em",
                      }}>
                        {PHASE_LABELS[phaseBreak].en}
                      </span>
                      <span style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        color: stepStyle.labelColor,
                        fontSize: "9px",
                        opacity: 0.7,
                      }}>
                        {PHASE_LABELS[phaseBreak].ja}
                      </span>
                    </div>
                    <div className="flex-1 h-px" style={{ background: stepStyle.borderColor }} />
                  </div>
                )}

                {/* ステップ本体 */}
                <div
                  className="flex gap-4"
                  style={{
                    opacity: isRevealed ? 1 : 0.15,
                    transform: isRevealed ? "translateX(0)" : "translateX(-10px)",
                    transition: "opacity 1s ease, transform 1s ease",
                  }}
                >
                  {/* 番号 + 縦ライン */}
                  <div className="flex flex-col items-center flex-shrink-0" style={{ width: "44px" }}>
                    <div style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: stepStyle.numberColor,
                      fontSize: "20px",
                      fontWeight: 300,
                      lineHeight: 1,
                      transition: "color 2s ease",
                      textShadow: isRevealed ? `0 0 16px ${stepStyle.numberColor}` : "none",
                    }}>
                      {step.number}
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div
                        className="flex-1 mt-2"
                        style={{
                          width: "1px",
                          minHeight: "32px",
                          background: stepStyle.lineColor,
                          transition: "background 2s ease",
                        }}
                      />
                    )}
                  </div>

                  {/* テキスト */}
                  <div className="flex-1 pb-1">
                    <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                      <span style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        color: stepStyle.titleColor,
                        fontSize: "15px",
                        fontWeight: 400,
                        transition: "color 2s ease",
                      }}>
                        {step.title}
                      </span>
                      <span style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        color: stepStyle.labelColor,
                        fontSize: "8.5px",
                        letterSpacing: "0.3em",
                        opacity: 0.65,
                        transition: "color 2s ease",
                      }}>
                        {step.subtitle}
                      </span>
                    </div>
                    <div style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: stepStyle.textColor,
                      fontSize: "12px",
                      lineHeight: 1.9,
                      letterSpacing: "0.04em",
                      transition: "color 2s ease",
                    }}>
                      {step.text}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 契約文 */}
        <div className="mt-14 mb-6 text-center">
          <div className="mx-auto mb-6" style={{
            width: "50px", height: "1px",
            background: style.borderColor,
            transition: "background 2s ease",
          }} />
          <p style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: style.textColor,
            fontSize: "13px",
            lineHeight: 2.2,
            letterSpacing: "0.07em",
            transition: "color 2s ease",
          }}>
            私は、この叡智を<br />
            <span style={{ color: style.numberColor, transition: "color 2s ease" }}>
              自分自身と、大切な人たちのためだけに
            </span>使う。<br />
            世界が平和になり、愛と自由のために——<br />
            このリスクを犯すことを、ここに誓う。
          </p>
          <div className="mx-auto mt-6" style={{
            width: "50px", height: "1px",
            background: style.borderColor,
            transition: "background 2s ease",
          }} />
        </div>
      </div>

      {/* ── 署名エリア ── */}
      <div
        className="flex-shrink-0 pt-4 border-t"
        style={{ borderColor: style.borderColor, transition: "border-color 2s ease" }}
      >
        {/* プログレスバー */}
        {!allRead && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5" style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: style.labelColor,
              fontSize: "8.5px",
              letterSpacing: "0.4em",
              transition: "color 2s ease",
            }}>
              <span>啓示を受け取る</span>
              <span>{Math.round(readProgress * 100)}%</span>
            </div>
            <div className="w-full overflow-hidden" style={{ height: "1px", background: "rgba(255,255,255,0.06)" }}>
              <div style={{
                width: `${readProgress * 100}%`,
                height: "100%",
                background: style.numberColor,
                transition: "width 0.3s ease, background 2s ease",
              }} />
            </div>
          </div>
        )}

        {/* 署名ボタン */}
        <button
          onClick={handleSign}
          disabled={!allRead || signingIn}
          className="w-full relative cursor-pointer whitespace-nowrap"
          style={{
            background: "transparent",
            border: `1px solid ${allRead ? style.borderColor : "rgba(255,255,255,0.06)"}`,
            padding: "13px 0",
            transition: "border-color 1s ease, opacity 0.5s ease",
            opacity: allRead && !signingIn ? 1 : allRead ? 0.5 : 0.22,
          }}
        >
          <div className="flex items-center justify-center gap-4">
            <div style={{
              width: "20px", height: "1px",
              background: allRead ? style.numberColor : "rgba(255,255,255,0.1)",
              transition: "background 1s ease",
            }} />
            <span style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: agreed ? "rgba(255,248,220,0.9)" : allRead ? style.numberColor : "rgba(255,255,255,0.2)",
              fontSize: "10.5px",
              letterSpacing: "0.45em",
              transition: "color 1s ease",
            }}>
              {agreed
                ? "COVENANT SEALED · 天界との契約、成立"
                : allRead
                ? "天界の契約に署名する"
                : "すべての啓示を受け取るまで、契約は結べない"}
            </span>
            <div style={{
              width: "20px", height: "1px",
              background: allRead ? style.numberColor : "rgba(255,255,255,0.1)",
              transition: "background 1s ease",
            }} />
          </div>
          {agreed && (
            <div className="absolute inset-0" style={{
              background: style.labelBg,
              animation: "signFlash 2s ease forwards",
            }} />
          )}
        </button>

        {!allRead && (
          <p className="text-center mt-2" style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "rgba(255,255,255,0.1)",
            fontSize: "8.5px",
            letterSpacing: "0.3em",
            fontStyle: "italic",
          }}>
            — 神々は、すべてを見届けている —
          </p>
        )}
      </div>

      <style>{`
        @keyframes signFlash {
          0%   { opacity: 0; }
          20%  { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
